import { Cart } from '@features/cart/api/get-cart'
import { centAmountToAmountV2 } from '@lib/format-price'
import { getOrigin } from '@lib/get-origin'
import { generateMediaUrlV2 } from '@lib/utils'
import { SendGridMailingList } from 'pages/api/email-sign-up'
import { mapEdgesToItems } from './map'

declare global {
  interface Window {
    dataLayer: any
  }
}

/* ---- Default Types  ---- */
// ADD_TO_CART
// DETAILS              lander på produkt
// DETAILS (IGEN)       når man skifter variant
// REMOVE_FROM_CART
// CHECKOUT             tryk på CHECKOUT knap
// PURCHASE             Purchase (Success page)

enum EventTypes {
  ADD_TO_BASKET = 'eec.add',
  REMOVE_FROM_BASKET = 'eec.remove',
  UPDATE_BASKET_QTY = 'eec.update',
  SCROLL_BUY_BANNER = 'eec.scrollBuyBannerr',
  CLOSE_BUY_BANNER = 'eec.closeBuyBanner',
  DETAIL_PRODUCT = 'eec.detail',
  CART_VIEW = 'eec.cart',
  CHECKOUT = 'eec.checkout',
  CHECKOUT_OPTION = 'eec.checkout_option',
  PURCHASE = 'eec.purchase',
  NEWSLETTER_SIGNUP = 'newsletter',
  IMPRESSION_CLICK = 'eec.impressionClick',
  ADD_TO_WISHLIST = 'eec.addToWishlist',
}

export enum CurrencyCodes {
  DKK = 'DKK',
  EUR = 'EUR',
  USD = 'USD',
}

interface ITrackingEvent {
  event: EventTypes
}
export interface IUserData {
  email?: string
  firstName?: string
  lastName?: string
  phone?: string
  city?: string
  postalCode?: string
  state?: string
  country?: string
}
interface IEcommerceTrackingEvent<T> extends ITrackingEvent {
  ecommerce: T & { currencyCode?: CurrencyCodes | string }
  cart?: IEcommerceProduct[]
  userData?: IUserData
}

export interface IEcommerceProduct {
  id?: string
  name?: string
  category?: string
  brand?: string
  variant?: string
  quantity?: number
  price?: string
  dimension1: string
  dimension2?: string
  image_url?: string
  url?: string
}

/* --- Add to wishlist implementation --- */
interface IWishlistTrackingParams {
  products: IEcommerceProduct[]
  trackinglist: string
}

interface IAddToWishlistTrackingEvent
  extends IEcommerceTrackingEvent<{
    addToWishlist: {
      actionField: {
        list: string
      }
      products?: IEcommerceProduct[] | undefined
    }
  }> {}

const generateAddToWishlistTrackingEvent = ({
  products,
  trackinglist,
}: IWishlistTrackingParams): IAddToWishlistTrackingEvent => {
  return {
    event: EventTypes.ADD_TO_WISHLIST,
    ecommerce: {
      addToWishlist: {
        actionField: { list: trackinglist },
        products,
      },
    },
  }
}

/* --- Impression click implementation --- */
interface IImpressionTrackingParams {
  products: IEcommerceProduct[]
  trackinglist: string
}

interface IImpressionTrackingEvent
  extends IEcommerceTrackingEvent<{
    click: {
      actionField: {
        list: string
      }
      products?: IEcommerceProduct[] | undefined
    }
  }> {}

const genereateImpressionTrackingEvent = ({
  products,
  trackinglist,
}: IImpressionTrackingParams): IImpressionTrackingEvent => {
  return {
    event: EventTypes.IMPRESSION_CLICK,
    ecommerce: {
      click: {
        actionField: { list: trackinglist },
        products,
      },
    },
  }
}

/* --- Purchase implementation --- */
interface IPurchaseTrackingParams {
  id: string
  revenue: string
  products: IEcommerceProduct[]
  currencyCode: CurrencyCodes | string
  userData: IUserData
  coupon?: string
  tax: string
  shipping: string
}

interface IPurchaseTrackingEvent
  extends IEcommerceTrackingEvent<{
    purchase?: {
      actionField: {
        id: string
        revenue: string
        tax: string
        shipping: string
        coupon?: string
      }
      products?: IEcommerceProduct[] | undefined
    }
  }> {}

const generatePurchaseTrackingEvent = ({
  products,
  revenue,
  id,
  currencyCode,
  userData,
  coupon,
  tax,
  shipping,
}: IPurchaseTrackingParams): IPurchaseTrackingEvent => {
  return {
    event: EventTypes.PURCHASE,
    ecommerce: {
      currencyCode,
      purchase: {
        actionField: {
          id,
          revenue,
          tax,
          shipping,
          ...(coupon ? { coupon: coupon } : {}),
        },
        products,
      },
    },
    userData: userData,
  }
}

/* ---- Newsletter Signup Event --- */
interface INewsletterTrackingEvent extends ITrackingEvent {
  newsletter: {
    private: boolean
    professional: boolean
    partner: boolean
  }
  userData: {
    email: string
  }
}

interface INewsletterTrackingParams {
  email: string
  newsletter: SendGridMailingList
}
const generateNewsletterEvent = ({
  email,
  newsletter,
}: INewsletterTrackingParams): INewsletterTrackingEvent => {
  return {
    event: EventTypes.NEWSLETTER_SIGNUP,
    newsletter: {
      private: newsletter === 'CONSUMERS',
      professional: newsletter == 'PROFESSIONALS',
      partner: newsletter == 'PARTNERS',
    },
    userData: {
      email: email,
    },
  }
}

interface ICartTrackingParams {
  products?: IEcommerceProduct[]
  currencyCode?: string
}
interface ICartTrackingEvent
  extends IEcommerceTrackingEvent<{
    cart?: {
      products?: IEcommerceProduct[] | undefined
    }
  }> {}

const generateCartViewTrackingEvent = ({
  products,
  currencyCode,
}: ICartTrackingParams): ICartTrackingEvent => {
  return {
    event: EventTypes.CART_VIEW,
    ecommerce: {
      currencyCode: currencyCode,
      cart: {
        products,
      },
    },
  }
}
/* ---- Checkout implementation --- */
interface ICheckoutTrackingParams {
  products?: IEcommerceProduct[]
  step: number
  option?: string
  userData?: IUserData
  currencyCode?: string
}
interface ICheckoutTrackingEvent
  extends IEcommerceTrackingEvent<{
    checkout?: {
      actionField: { step: number; option?: string }
      products?: IEcommerceProduct[] | undefined
    }
    checkout_option?: {
      actionField: { step: number; option?: string }
    }
  }> {}

const generateCheckoutTrackingEvent = ({
  products,
  step,
  option,
  userData,
  currencyCode,
}: ICheckoutTrackingParams): ICheckoutTrackingEvent => {
  return {
    event: EventTypes.CHECKOUT,
    ecommerce: {
      currencyCode: currencyCode,
      checkout: {
        actionField: { step, option },
        products,
      },
    },
    ...(userData ? { userData: userData } : {}),
  }
}

/* ---- Add to basket implementation ---- */
interface IBasketTrackingParams {
  currencyCode?: CurrencyCodes
  products?: IEcommerceProduct[]
  trackingList?: string
  cart?: Cart
}

interface IAddToBasketTrackingEvent
  extends IEcommerceTrackingEvent<{
    add: {
      actionField: { list?: string }
      products: IEcommerceProduct[] | undefined
    }
  }> {}

const generateAddToBasketTrackingEvent = ({
  products,
  trackingList,
  currencyCode,
  cart,
}: IBasketTrackingParams): IAddToBasketTrackingEvent => {
  const lineItems = mapEdgesToItems(cart?.lineItems) ?? []
  return {
    event: EventTypes.ADD_TO_BASKET,
    ecommerce: {
      currencyCode: currencyCode,
      add: {
        actionField: {},
        products: products,
      },
    },
    cart:
      lineItems?.map((lineItem) => ({
        id: lineItem.variant.product.id,
        name: lineItem.variant.product.name,
        variant: lineItem.variant.name,
        category:
          lineItem.variant.product.tags.length > 0 &&
          lineItem.variant.product.tags
            ?.find((tag) => tag?.startsWith('type_'))
            ?.split('_')[1]
            .replace(/-/g, ''),
        brand: lineItem.variant.product.vendor,
        dimension1: lineItem.variant.sku,
        quantity: lineItem.quantity,
        price: centAmountToAmountV2(
          lineItem.unitPrice.discountedPrice
            ? lineItem.unitPrice.discountedPrice.value
            : lineItem.unitPrice.value,
        ).toString(),
      })) ?? [],
  }
}

interface IRemoveFromBasketTrackingEvent
  extends IEcommerceTrackingEvent<{
    remove: {
      actionField: { list?: string }
      products: IEcommerceProduct[] | undefined
    }
  }> {}

const generateRemoveFromBasketTrackingEvent = ({
  products,
  trackingList,
  currencyCode,
}: IBasketTrackingParams): IRemoveFromBasketTrackingEvent => {
  return {
    event: EventTypes.REMOVE_FROM_BASKET,
    ecommerce: {
      currencyCode: currencyCode,
      remove: {
        actionField: {},
        products: products,
      },
    },
  }
}

interface IUpdateQuantityBasketTrackingEvent
  extends IEcommerceTrackingEvent<{
    update: {
      actionField: { list?: string }
      products: IEcommerceProduct[] | undefined
    }
  }> {}

const generateUpdateQuantityBasketTrackingEvent = ({
  products,
  trackingList,
  currencyCode,
}: IBasketTrackingParams): IUpdateQuantityBasketTrackingEvent => {
  return {
    event: EventTypes.UPDATE_BASKET_QTY,
    ecommerce: {
      currencyCode: currencyCode,
      update: {
        actionField: {},
        products: products,
      },
    },
  }
}

/* ---- tracking event for Buy Banner  ---- */
interface IViewProductTrackingParams {
  currencyCode?: CurrencyCodes | string
  products: IEcommerceProduct[]
  trackingList: string
}

interface IProductViewTrackingEvent
  extends IEcommerceTrackingEvent<{
    detail: {
      actionField: { list?: string }
      products: IEcommerceProduct[]
    }
  }> {}

const generateProductViewTrackingEvent = ({
  products,
  trackingList,
  currencyCode,
}: IViewProductTrackingParams): IProductViewTrackingEvent => {
  return {
    event: EventTypes.DETAIL_PRODUCT,
    ecommerce: {
      currencyCode: currencyCode,
      detail: {
        actionField: {},
        products: products,
      },
    },
  }
}

/* ---- tracking event for Buy Banner  ---- */
interface IBuyBannerTrackingParams {
  product?: any
  trackingList: string
}

interface IBuyBannerScrollTrackingEvent
  extends IEcommerceTrackingEvent<{
    scroll: {
      actionField: {}
      product: any | undefined
    }
  }> {}

const generateBuyBannerScrollEvent = ({
  product,
  trackingList,
}: IBuyBannerTrackingParams): IBuyBannerScrollTrackingEvent => {
  return {
    event: EventTypes.SCROLL_BUY_BANNER,
    ecommerce: {
      scroll: {
        actionField: {},
        product: product,
      },
    },
  }
}

const generateBuyBannerCloseEvent = ({
  product,
  trackingList,
}: IBuyBannerTrackingParams): IBuyBannerScrollTrackingEvent => {
  return {
    event: EventTypes.CLOSE_BUY_BANNER,
    ecommerce: {
      scroll: {
        actionField: {},
        product: product,
      },
    },
  }
}

export const getTrackingImageUrlV2 = (medias: string[]): string => {
  //filter out videos
  const images = medias.filter((m) => !m.includes('.mp4'))

  //return the image with lowest position
  return generateMediaUrlV2(images[0])?.replace('{size}', '1080')
}

export const getTrackingProductUrl = (
  slug: string,
  itemNumber: string | number,
): string => {
  return `${getOrigin()}/products/${slug}?itemNumber=${itemNumber}`
}

export const getProductCategory = (tags: string[]): string => {
  if (tags.length <= 0) return ''
  const isOutDoor = tags.find((tag) => tag == 'cat_outdoor')
  const filteredTags = tags.filter((tag) => tag !== 'cat_outdoor')

  const category = filteredTags
    .find((tag) => tag.startsWith('cat_'))
    ?.split('_')[1]
    ?.replace(/-/g, '')

  const subCategory = filteredTags
    .find((tag) => tag.startsWith('type_'))
    ?.split('_')[1]
    ?.replace(/-/g, '')

  if (isOutDoor) {
    return `Outdoor ${category}${subCategory ? `/${subCategory}` : ''}`
  }

  return `${category}${subCategory ? `/${subCategory}` : ''}`
}
/* ---- Default Exports ---- */
const ensureDataLayer = () => {
  if (typeof window === 'undefined') {
    return false
  }

  window.dataLayer = window.dataLayer || []
  return true
}

export const removeSpecialCharactersFromPhoneNumber = (phone?: string) => {
  if (!phone) return ''
  return phone.replace(/[\s()-]/g, '')
}

const pushTrackingEvent = (trackingEvent: ITrackingEvent) => {
  if (ensureDataLayer()) window.dataLayer?.push(trackingEvent)
}

const pushEcommerenceTrackingEvent = (
  trackingEvent: IEcommerceTrackingEvent<unknown>,
) => {
  pushTrackingEvent(trackingEvent)
}
export const pushImpressionTrackingEvent = (
  props: IImpressionTrackingParams,
) => {
  pushEcommerenceTrackingEvent(genereateImpressionTrackingEvent(props))
}
export const pushPurchaseTrackingEvent = (props: IPurchaseTrackingParams) => {
  pushEcommerenceTrackingEvent(generatePurchaseTrackingEvent(props))
}
export const pushCheckoutTrakcingEvent = (props: ICheckoutTrackingParams) => {
  pushEcommerenceTrackingEvent(generateCheckoutTrackingEvent(props))
}
export const pushAddToBasketTrackingEvent = (props: IBasketTrackingParams) =>
  pushEcommerenceTrackingEvent(generateAddToBasketTrackingEvent(props))

export const pushUpdateBasketQuantityTrackingEvent = (
  props: IBasketTrackingParams,
) =>
  pushEcommerenceTrackingEvent(generateUpdateQuantityBasketTrackingEvent(props))

export const pushRemoveFromBasketTrackingEvent = (
  props: IBasketTrackingParams,
) => pushEcommerenceTrackingEvent(generateRemoveFromBasketTrackingEvent(props))

export const pushBuyBannerScrollTrackingEvent = (
  props: IBuyBannerTrackingParams,
) => pushEcommerenceTrackingEvent(generateBuyBannerScrollEvent(props))

export const pushBuyBannerCloseTrackingEvent = (
  props: IBuyBannerTrackingParams,
) => pushEcommerenceTrackingEvent(generateBuyBannerCloseEvent(props))

export const pushProductViewTrackingEvent = (
  props: IViewProductTrackingParams,
) => pushEcommerenceTrackingEvent(generateProductViewTrackingEvent(props))

export const pushNewsletterTrackingEvent = (props: INewsletterTrackingParams) =>
  pushTrackingEvent(generateNewsletterEvent(props))

export const pushAddToWishlistTrackingEvent = (
  props: IWishlistTrackingParams,
) => pushTrackingEvent(generateAddToWishlistTrackingEvent(props))

export const pushCartViewTrackingEvent = (props: ICartTrackingParams) =>
  pushTrackingEvent(generateCartViewTrackingEvent(props))
